/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '../../components/layout/Header';
import VerdictBadge from '../../components/finops/VerdictBadge';
import { finopsService } from '../../services/finopsApi';
import { formatDate, formatMicroUSD, savingsColorClass } from '../../components/finops/finopsFormat';

const StatCard: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="card">
    <p className="text-sm text-gray-400">{label}</p>
    <p className="text-2xl font-bold text-white mt-1">{value}</p>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const CreditsGauge: React.FC<{
  remaining: number;
  total: number;
  forecastExhaustionAt?: string | null;
}> = ({ remaining, total, forecastExhaustionAt }) => {
  if (remaining === -1 || total <= 0) {
    return (
      <div className="card">
        <p className="text-sm text-gray-400 mb-3">Credits</p>
        <p className="text-sm text-gray-300 mb-3">
          Set your AWS credits total to track burn rate and estimated exhaustion date.
        </p>
        <Link to="/finops/connections" className="btn-secondary btn-sm inline-block">
          Set your credits total
        </Link>
      </div>
    );
  }

  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  const barColor = pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="card">
      <p className="text-sm text-gray-400 mb-3">Credits remaining</p>
      <p className="text-2xl font-bold text-white">{formatMicroUSD(remaining)}</p>
      <p className="text-xs text-gray-500 mb-3">of {formatMicroUSD(total)} total</p>
      <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {forecastExhaustionAt ? `Estimated exhaustion ${formatDate(forecastExhaustionAt)}` : 'Not enough burn history yet to forecast exhaustion'}
      </p>
    </div>
  );
};

const FinopsDashboard: React.FC = () => {
  const [connectionId, setConnectionId] = useState<string>('');

  const { data: connections } = useQuery({
    queryKey: ['finops-connections'],
    queryFn: () => finopsService.listConnections().then((res) => res.data.connections),
  });

  useEffect(() => {
    if (!connectionId && connections && connections.length > 0) {
      setConnectionId(connections[0].id);
    }
  }, [connections, connectionId]);

  const { data: summary, isLoading, isError, refetch } = useQuery({
    queryKey: ['finops-summary', connectionId],
    queryFn: () => finopsService.getSummary(connectionId).then((res) => res.data),
    enabled: !!connectionId,
  });

  const selectedConnection = connections?.find((c) => c.id === connectionId);

  return (
    <div>
      <Header title="FinOps Dashboard" subtitle="30-day AWS spend, credit burn and top savings opportunities" />

      <div className="p-6 space-y-6">
        {!connections || connections.length === 0 ? (
          <div className="card">
            <div className="text-center py-12">
              <h3 className="text-sm font-medium text-white">No AWS connections yet</h3>
              <p className="mt-1 text-sm text-gray-400 max-w-md mx-auto">
                Connect an AWS account to see spend and recommendations here.
              </p>
              <Link to="/finops/connections" className="btn-primary inline-flex items-center gap-2 mt-4">
                Connect AWS account
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="max-w-xs">
              <label htmlFor="finops-dashboard-connection" className="block text-sm font-medium text-gray-300 mb-2">
                Connection
              </label>
              <select
                id="finops-dashboard-connection"
                className="input"
                value={connectionId}
                onChange={(e) => setConnectionId(e.target.value)}
              >
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.status})
                  </option>
                ))}
              </select>
            </div>

            {isLoading ? (
              <div className="card">
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4" />
                  <p className="text-sm text-gray-400">Loading summary...</p>
                </div>
              </div>
            ) : isError ? (
              <div className="card">
                <div className="text-center py-12">
                  <p className="text-red-400 mb-4">Failed to load the dashboard summary</p>
                  <button onClick={() => refetch()} className="btn-primary">
                    Retry
                  </button>
                </div>
              </div>
            ) : summary ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard label="Billed (30d)" value={formatMicroUSD(summary.spend_30d.billed_micro_usd)} />
                  <StatCard label="Effective (30d)" value={formatMicroUSD(summary.spend_30d.effective_micro_usd)} hint="After credits applied" />
                  <StatCard label="Credits applied (30d)" value={formatMicroUSD(summary.spend_30d.credits_micro_usd)} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CreditsGauge
                    remaining={summary.credit_status.credits_remaining_micro_usd}
                    total={selectedConnection?.credits_total_micro_usd || 0}
                    forecastExhaustionAt={summary.credit_status.forecast_exhaustion_at}
                  />

                  <div className="card">
                    <p className="text-sm text-gray-400 mb-3">Resources by service</p>
                    {Object.keys(summary.resource_counts).length === 0 ? (
                      <p className="text-sm text-gray-500">No inventory synced yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(summary.resource_counts).map(([service, count]) => (
                          <div key={service} className="flex items-center justify-between text-sm">
                            <span className="text-gray-300 uppercase">{service}</span>
                            <span className="text-gray-400">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-400">Top recommendations</p>
                    <Link to="/finops/recommendations" className="text-xs text-primary-400 hover:text-primary-300">
                      View all
                    </Link>
                  </div>
                  {summary.top_recommendations.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No recommendations yet - run Analyze from the Connections page after your first sync.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {summary.top_recommendations.map((rec) => (
                        <div key={rec.id} className="flex items-center justify-between gap-4 border-b border-gray-800 pb-3 last:border-0 last:pb-0">
                          <div className="min-w-0">
                            <VerdictBadge verdict={rec.verdict} migrateAt={rec.migrate_at} />
                            <p className="text-xs text-gray-500 mt-1 truncate">{rec.explanation}</p>
                          </div>
                          <span className={`text-sm font-medium whitespace-nowrap ${savingsColorClass(rec.monthly_savings_micro_usd)}`}>
                            {formatMicroUSD(rec.monthly_savings_micro_usd)}/mo
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default FinopsDashboard;
